<template>
  <div class="flex gap-1">
    <ProductCardBadge
      v-for="(badge, i) in badges"
      :key="i"
      :badge-class="badge.badgeClass"
      :badge-translation-key="badge.badgeTranslationKey"
    />
  </div>
</template>

<script lang="ts">

import type { ProductTag } from '~~/types/product/attribute/ProductTag';
import { PRODUCT_TAG } from '~~/types/product/attribute/ProductTag';
import type { ProductBadge } from '~~/types/product/ProductBadge';
import { MAX_AMOUNT_OF_BADGES } from '~~/constants/numbers';

export default defineComponent({
  name: 'ProductCardBadges',

  props: {
    tags: {
      type: Array as PropType<ProductTag[]>,
      required: true,
    },
  },
  setup (props) {

    const tagToClassMap = {
      [PRODUCT_TAG.CustomBadge]: 'product-badge--custom-badge',
      [PRODUCT_TAG.New]: 'product-badge--new',
      [PRODUCT_TAG.Popular]: 'product-badge--popular',
      [PRODUCT_TAG.Sale]: 'product-badge--sale',
      [PRODUCT_TAG.Bware]: 'product-badge--bware',
      [PRODUCT_TAG.TopRated]: 'product-badge--top-rated',
      [PRODUCT_TAG.BlackFriday]: 'product-badge--black-friday',
      [PRODUCT_TAG.CyberWeek]: 'product-badge--cyber-week',
    };
    // TODO - decide whether we want to switch to 'translations accessed directly in setup' or keep it like this.
    const tagToTextMap = {
      [PRODUCT_TAG.CustomBadge]: '50% Boost',
      [PRODUCT_TAG.New]: 'New Product',
      [PRODUCT_TAG.Popular]: 'Popular Product',
      [PRODUCT_TAG.Sale]: 'Product On Sale',
      [PRODUCT_TAG.Bware]: 'B-Goods Product',
      [PRODUCT_TAG.TopRated]: 'Top Rated Product',
      [PRODUCT_TAG.BlackFriday]: 'BLACK WEEK',
      [PRODUCT_TAG.CyberWeek]: 'CYBER WEEK',
    };

    const priorityOrder: ProductTag[] = [
      PRODUCT_TAG.CustomBadge,
      PRODUCT_TAG.Sale,
      PRODUCT_TAG.CyberWeek,
      PRODUCT_TAG.BlackFriday,
      PRODUCT_TAG.Bware,
      PRODUCT_TAG.Popular,
      PRODUCT_TAG.New,
      PRODUCT_TAG.TopRated,
    ];

    const getBadge = (productTag: ProductTag): ProductBadge | undefined => {

      if (!productTag) {

        return;
      
      }

      const badgeClass = tagToClassMap[productTag];
      const badgeTranslationKey = tagToTextMap[productTag];

      if (badgeClass && badgeTranslationKey) {

        return {
          badgeClass,
          badgeTranslationKey,
        };
      
      }
    
    };

    const sortTagsByPriority = (tags: ProductTag[]): ProductTag[] => {

      const excludeCustomTags = tags.filter(tag => priorityOrder.includes(tag));

      return excludeCustomTags.sort((tagA, tagB) => {

        const priorityA = priorityOrder.indexOf(tagA);
        const priorityB = priorityOrder.indexOf(tagB);

        return priorityA - priorityB;
      
      });
    
    };

    const sortedTags = computed(() => {

      if (!props.tags || props.tags.length < 2) {

        return props.tags || [];
      
      }

      const tags = props.tags.slice();

      return sortTagsByPriority(tags);
    
    });

    const badges = computed(() => {

      const badgeArray = [];

      for (const tag of sortedTags.value) {

        const badge = getBadge(tag);

        if (badge) {

          badgeArray.push(badge);
        
        }

        if (badgeArray.length === MAX_AMOUNT_OF_BADGES) {

          break;
        
        }
      
      }

      return badgeArray;
    
    });

    return {
      badges,
    };
  
  },
});

</script>
